/**
 * Maps Main-sheet course-raw + school level to Acuity-style taxonomy codes.
 * Mirrors code-gs.js generateStandardCourseCodes; semester is 'S1' | 'S2' (not hard-coded S1).
 */

export function inferIsMiddleSchool(schoolLevelRaw, gradeLevelRaw) {
  const sl = (schoolLevelRaw || '').toString().trim();
  if (sl) {
    const gradeNum = parseInt(sl, 10);
    if (sl.toLowerCase().includes('middle')) return true;
    if (!Number.isNaN(gradeNum) && gradeNum <= 8) return true;
    if (sl.toLowerCase().includes('high')) return false;
  }
  const g = (gradeLevelRaw || '').toString().toLowerCase();
  if (['6', '7', '8', '6th', '7th', '8th'].some(x => g.includes(x))) return true;
  return false;
}

/**
 * @param {string} courseRaw
 * @param {string} schoolLevelRaw - "School Level" column from Main
 * @param {string} gradeLevelRaw - optional "gradeLevel" when School Level empty
 * @param {'S1'|'S2'} semester
 * @returns {string} code or "UNKNOWN"
 */
export function courseRawToTaxonomyCode(courseRaw, schoolLevelRaw, gradeLevelRaw, semester) {
  const raw = (courseRaw || '').toString().trim();
  if (!raw) return '';

  const c = raw.toLowerCase();
  const isMS = inferIsMiddleSchool(schoolLevelRaw, gradeLevelRaw);
  const sem = semester === 'S2' ? 'S2' : 'S1';

  let code = 'UNKNOWN';

  if (c.includes('film production ii') || c.includes('cte film production ii')) {
    code = `VP1A*II.HS.${sem}.2526`;
  } else if (c.includes('recording technology ii')) {
    code = `RT1A*II.HS.${sem}.2526`;
  } else if (c.includes('photography ii')) {
    code = `PH2A.HS.${sem}.2526`;
  } else if (c.includes('media broadcasting ii')) {
    code = `MB2A.HS.${sem}.2526`;
  } else if (c.includes('cte graphic design ii')) {
    code = `GD2A.HS.${sem}.2526`;
  } else if (c.includes('digital marketing ii')) {
    code = `DM2A.HS.${sem}.2526`;
  } else if (c.includes('film production a') || c.includes('cte film production i')) {
    code = isMS ? `VP1A*I.MS.${sem}.2526` : `VP1A*I.HS.${sem}.2526`;
  } else if (c.includes('recording technology a') || c.includes('recording technology i')) {
    code = isMS ? `RT1A*I.MS.${sem}.2526` : `RT1A*I.HS.${sem}.2526`;
  } else if (c.includes('photography a') || c.includes('photography i')) {
    code = isMS ? `PH1A.MS.${sem}.2526` : `PH1A.HS.${sem}.2526`;
  } else if (c.includes('media broadcasting a') || c.includes('media broadcasting i')) {
    code = isMS ? `MB1A.MS.${sem}.2526` : `MB1A.HS.${sem}.2526`;
  } else if (c.includes('graphic design a') || c.includes('graphic design i')) {
    code = isMS ? `GD1A.MS.${sem}.2526` : `GD1A.HS.${sem}.2526`;
  } else if (c.includes('digital marketing a') || c.includes('digital marketing i')) {
    code = isMS ? `DM1A.MS.${sem}.2526` : `DM1A.HS.${sem}.2526`;
  }

  return code;
}

/** @returns {'s1'|'s2'|null} */
export function semesterHintFromCourseGroupTitle(title) {
  const t = (title || '').toString();
  if (/\bS2\b|\.S2\.|semester\s*2|sem\.?\s*2\b/i.test(t)) return 's2';
  if (/\bS1\b|\.S1\.|semester\s*1|sem\.?\s*1\b/i.test(t)) return 's1';
  return null;
}

/**
 * For S2-only mode: drop enrollments tied to a course group whose title is clearly S1.
 * Keeps: no coursegroup, S2-titled groups, or ambiguous titles.
 */
export function filterEnrollmentsForSemesterComparator(enrollments, courseGroupMap, semesterMode) {
  if (semesterMode !== 's2_only') return enrollments;

  return enrollments.filter(ce => {
    if (!ce.coursegroup) return true;
    const title = courseGroupMap.get(ce.coursegroup);
    if (!title) return true;
    const hint = semesterHintFromCourseGroupTitle(title);
    if (hint === 's1') return false;
    return true;
  });
}

/**
 * True when the student has at least one course-group enrollment and every grouped
 * enrollment is in a clearly S1-titled group. Ungrouped enrollments (no coursegroup)
 * do not block this — e.g. an extra loose course row alongside an S1 group still counts
 * as "already in S1" for the S2 comparator.
 * False if there is no grouped enrollment, or any group is S2/ambiguous.
 */
export function studentIsS1OnlyForS2Comparator(activeEnrollments, courseGroupMap) {
  const grouped = activeEnrollments.filter(ce => ce.coursegroup);
  if (grouped.length === 0) return false;
  return grouped.every(ce => {
    const title = courseGroupMap.get(ce.coursegroup);
    return semesterHintFromCourseGroupTitle(title) === 's1';
  });
}

export function enrollmentMatchesTaxonomyCode(ce, expectedCode, courseGroupMap) {
  if (!expectedCode || expectedCode === 'UNKNOWN') return false;
  const exp = expectedCode.toLowerCase();
  const gTitle = (courseGroupMap.get(ce.coursegroup) || '').toString().toLowerCase();
  if (gTitle && (gTitle === exp || gTitle.includes(exp))) return true;
  const ct = (ce.course_title || '').toString().toLowerCase();
  if (ct && (ct === exp || ct.includes(exp))) return true;
  return false;
}

/**
 * Split CSV or TSV (from Sheets paste) respecting double-quoted fields.
 * Delimiter is chosen from the first line: more tabs than commas → tab.
 */
export function parseDelimitedRows(text) {
  const s = text.replace(/^\uFEFF/, '');
  const firstBreak = s.search(/\r\n|\r|\n/);
  const firstLine = firstBreak === -1 ? s : s.slice(0, firstBreak);
  const tabCount = (firstLine.match(/\t/g) || []).length;
  const commaCount = (firstLine.match(/,/g) || []).length;
  const delim = tabCount > commaCount ? '\t' : ',';

  const rows = [];
  let row = [];
  let cur = '';
  let inQuotes = false;

  for (let i = 0; i < s.length; i++) {
    const c = s[i];
    if (c === '"') {
      if (inQuotes && s[i + 1] === '"') {
        cur += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (!inQuotes && (c === '\n' || c === '\r')) {
      if (c === '\r' && s[i + 1] === '\n') i++;
      row.push(cur);
      rows.push(row.map(cell => cell.trim()));
      row = [];
      cur = '';
    } else if (!inQuotes && c === delim) {
      row.push(cur);
      cur = '';
    } else {
      cur += c;
    }
  }
  row.push(cur);
  if (row.some(cell => cell.trim() !== '') || rows.length === 0) {
    rows.push(row.map(cell => cell.trim()));
  }
  return rows;
}

export function normalizeHeaderCell(h) {
  return (h || '')
    .toString()
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
}

const compactHeader = (s) => normalizeHeaderCell(s).replace(/[\s_-]/g, '');

/** @param {string[]} headerRow */
export function findColumnIndex(headerRow, aliases) {
  const normHeaders = headerRow.map(normalizeHeaderCell);
  for (const alias of aliases) {
    const t = normalizeHeaderCell(alias);
    const i = normHeaders.indexOf(t);
    if (i !== -1) return i;
  }
  for (const alias of aliases) {
    const t = compactHeader(alias);
    const i = normHeaders.findIndex(h => compactHeader(h) === t);
    if (i !== -1) return i;
  }
  return -1;
}
